import { useEffect, useState } from 'react';

import { useCasesApi } from '@/lib/api';
import { isUseCaseDiscoverable } from '../../shared/public/use-cases.cjs';
import publicNavigation from '../../shared/public/navigation.cjs';

/**
 * Persona pages for navigation menus, from the same `/json/use-cases` list that
 * powers the hub page.
 *
 * Loaded once when the hook mounts (not when a menu opens) so the first hover
 * already has the full catalog of links. A previous version only fetched while
 * the panel was open and dropped the result if the menu closed before the
 * request finished —
 * which is why the dropdown showed "Browse all use cases" with no links.
 */

const CATEGORY_ORDER = ['values', 'business', 'creative', 'technical', 'education', 'lifestyle'];

const CATEGORY_LABELS = {
  values: 'Values and community',
  business: 'Business, sales and marketing',
  creative: 'Creative work',
  technical: 'Engineering and product',
  education: 'Education and research',
  lifestyle: 'Everyday life',
};

const NAVIGATION_SLUGS = new Set(publicNavigation.audiences.map(item=>item.href.split('/').pop()));

let cachedGroups = null;
let inflight = null;

function groupUseCases(useCases) {
  const byCategory = new Map();

  for (const useCase of useCases) {
    if (!useCase?.slug || !useCase?.name || !isUseCaseDiscoverable(useCase) || !NAVIGATION_SLUGS.has(useCase.slug)) continue;
    const category = useCase.hubCategory || 'business';
    if (!byCategory.has(category)) byCategory.set(category, []);
    byCategory.get(category).push({
      slug: useCase.slug,
      name: useCase.name,
      href: `/use-cases/${useCase.slug}`,
      isFeatured: !!useCase.flags?.isFeatured,
    });
  }

  for (const items of byCategory.values()) items.sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured));
  const ordered = [];
  for (const category of CATEGORY_ORDER) {
    const items = byCategory.get(category);
    if (items?.length) {
      ordered.push({ category, label: CATEGORY_LABELS[category] || category, items });
      byCategory.delete(category);
    }
  }
  for (const [category, items] of byCategory) {
    if (items.length) {
      ordered.push({ category, label: CATEGORY_LABELS[category] || category, items });
    }
  }

  return ordered;
}

function loadUseCaseGroups() {
  if (cachedGroups) return Promise.resolve(cachedGroups);
  if (inflight) return inflight;

  inflight = useCasesApi.list({ summary: true })
    .then((data) => {
      const groups = groupUseCases(data?.useCases || []);
      if (groups.length) cachedGroups = groups;
      return groups;
    })
    .catch((err) => {
      console.warn('[useUseCaseMenu] failed to load use cases', err?.message || err);
      return [];
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

// Warm the cache as soon as this module loads on a public page.
loadUseCaseGroups();

export default function useUseCaseMenu() {
  const [groups, setGroups] = useState(() => cachedGroups || []);
  const [isLoading, setIsLoading] = useState(() => !cachedGroups);

  useEffect(() => {
    if (cachedGroups) {
      setGroups(cachedGroups);
      setIsLoading(false);
      return undefined;
    }

    let alive = true;
    setIsLoading(true);

    loadUseCaseGroups().then((next) => {
      if (!alive) return;
      setGroups(next);
      setIsLoading(false);
    });

    return () => { alive = false; };
  }, []);

  return { groups, isLoading };
}

export { CATEGORY_LABELS, CATEGORY_ORDER, groupUseCases };
